import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ShoppingBag, Loader2, Lightbulb } from "lucide-react";
import { useWallet } from "@solana/wallet-adapter-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { PageHero } from "@/components/PageHero";

import { SearchFilters } from "@/components/marketplace/SearchFilters";
import { NFTSectionCarousel } from "@/components/marketplace/NFTSectionCarousel";
import { useMarketplaceNFTs } from "@/hooks/useMarketplaceNFTs";

export default function EarnPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { connected } = useWallet();
  const {
    nfts,
    allNfts,
    collections,
    loading,
    search,
    setSearch,
    sortBy,
    setSortBy,
    priceRange,
    setPriceRange,
    selectedCollections,
    setSelectedCollections,
    refresh,
  } = useMarketplaceNFTs();

  // Initialize search from URL query parameter (only on mount)
  useEffect(() => {
    const searchQuery = searchParams.get("search");
    if (searchQuery) {
      setSearch(searchQuery);
      setSearchParams({}, { replace: true });
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AppHeader currentPage="earn" />
        <main className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading marketplace...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
      <AppHeader currentPage="earn" />

      <main className="flex-1 pt-20 pb-24 px-4">
        <div className="max-w-7xl mx-auto space-y-4">
          <PageHero
            title="NFT Marketplace"
            subtitle="Discover and collect unique digital assets"
          />

          {/* Pro Tip */}
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Lightbulb className="w-4 h-4 text-primary" />
            <span>
              Pro Tip: Share your NFT on social media to increase visibility and boost your chances of selling!
            </span>
          </div>

          <SearchFilters
            search={search}
            setSearch={setSearch}
            sortBy={sortBy}
            setSortBy={setSortBy}
            priceRange={priceRange}
            setPriceRange={setPriceRange}
            totalResults={nfts.length}
            collections={collections}
            selectedCollections={selectedCollections}
            setSelectedCollections={setSelectedCollections}
          />

          {/* Single results view - always filtered/sorted */}
          {nfts.length === 0 ? (
            <Card className="bg-card/80 backdrop-blur-sm border border-border/30 rounded-xl shadow-soft">
              <CardContent className="p-6 text-center">
                <div className="p-4 rounded-full bg-muted/50 w-fit mx-auto mb-4">
                  <ShoppingBag className="h-12 w-12 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-2">No NFTs Found</h3>
                <p className="text-muted-foreground mb-4">
                  {allNfts.length === 0 
                    ? "Be the first to create an NFT on FLOW Marketplace!" 
                    : "Try adjusting your search or filters"}
                </p>
                {connected && allNfts.length === 0 && (
                  <Button variant="default" onClick={() => navigate("/dashboard")}>
                    Create Your First NFT
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <NFTSectionCarousel
              title={`${nfts.length} NFTs`}
              icon={<ShoppingBag className="w-5 h-5 text-white" />}
              nfts={nfts.slice(0, 8)}
              showTrending={sortBy === "trending"}
              onBuySuccess={refresh}
            />
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}